import React from 'react';

interface ClockProps {
  time: Date;
  timeZone: string;
  label: string;
  offset: string;
}

const Clock: React.FC<ClockProps> = ({ time, timeZone, label, offset }) => {
  // Convert time to the target timezone
  const timeInZone = new Date(time.toLocaleString('en-US', { timeZone }));
  
  // Get hours, minutes, seconds in the target timezone
  const hours = timeInZone.getHours();
  const minutes = timeInZone.getMinutes();
  const seconds = timeInZone.getSeconds();
  const milliseconds = timeInZone.getMilliseconds();

  // Calculate rotation angles for clock hands
  const secondDegrees = (seconds + milliseconds / 1000) * 6; // 360 degrees / 60 seconds
  const minuteDegrees = (minutes + seconds / 60) * 6; // 360 degrees / 60 minutes
  const hourDegrees = (hours % 12 + minutes / 60) * 30; // 360 degrees / 12 hours

  // Format digital time in the target timezone
  const digitalTime = time.toLocaleTimeString('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Format date in the target timezone
  const digitalDate = time.toLocaleDateString('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-xs text-gray-400 dark:text-gray-500">{offset}</div>
      </div>
      
      {/* Analog Clock */}
      <div className="relative w-32 h-32 mx-auto mb-3">
        {/* Clock face */}
        <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700">
          {/* Hour markers */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-2 bg-gray-400 dark:bg-gray-500"
              style={{
                left: '50%',
                top: '4px',
                transform: `rotate(${i * 30}deg) translateX(-50%)`,
                transformOrigin: '50% 60px'
              }}
            />
          ))}
          
          {/* Hour hand */}
          <div
            className="absolute w-1 h-12 bg-gray-800 dark:bg-gray-300 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -100%) rotate(${hourDegrees}deg)`,
              transformOrigin: '50% 100%'
            }}
          />
          
          {/* Minute hand */}
          <div
            className="absolute w-1 h-16 bg-gray-600 dark:bg-gray-400 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -100%) rotate(${minuteDegrees}deg)`,
              transformOrigin: '50% 100%'
            }}
          />
          
          {/* Second hand */}
          <div
            className="absolute w-0.5 h-20 bg-red-500 rounded-full"
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -100%) rotate(${secondDegrees}deg)`,
              transformOrigin: '50% 100%'
            }}
          />
          
          {/* Center dot */}
          <div className="absolute w-3 h-3 bg-gray-800 dark:bg-gray-300 rounded-full left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
      </div>

      {/* Digital Time */}
      <div className="text-center">
        <div className="text-lg font-mono text-gray-900 dark:text-white">
          {digitalTime}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            .{milliseconds.toString().padStart(3, '0')}
          </span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {digitalDate}
        </div>
      </div>
    </div>
  );
};

export default Clock; 